// src/pages/Research.js
import React from "react";

// Import images
import research1 from "../assets/research1.webp";
import research2 from "../assets/research2.webp";
import research3 from "../assets/research3.webp";
import research4 from "../assets/research4.webp";
import research5 from "../assets/research5.jpg";
import research6 from "../assets/research6.webp";

// 🔹 Reusable Section Component
const SectionWithImages = ({
  id,
  title,
  text,
  images,
  reverse = false,
  labels = [],
  titleClassName = "",
}) => {
  return (
    <div
      id={id}
      className={`flex flex-col ${
        reverse ? "sm:flex-row-reverse" : "sm:flex-row"
      } items-center text-white bg-gray-900 p-12 sm:p-20 gap-8`}
    >
      {/* Text Section */}
      <div
        className={`w-full ${
          images.length === 0 ? "text-center" : "sm:w-1/2 text-left"
        }`}
      >
        <h2
          className={`text-4xl sm:text-5xl font-bold mb-6 animate-fadeIn ${titleClassName}`}
        >
          {title}
        </h2>
        <p className="text-lg sm:text-xl animate-fadeIn animate-delay-300 text-justify whitespace-pre-line">
          {text || "More details coming soon."}
        </p>
      </div>

      {/* Images Section */}
      {images.length > 0 && (
        <div
          className={`w-full sm:w-1/2 grid grid-cols-1 ${
            images.length > 1 ? "sm:grid-cols-2" : ""
          } gap-4`}
        >
          {images.map((img, index) => (
            <div key={index} className="flex flex-col items-center">
              <img
                src={img}
                alt={`${title} ${labels[index] || "Visual"}`}
                className="rounded-lg shadow-lg transform hover:scale-105 transition duration-300 w-full h-64 sm:h-80 object-cover"
              />
              {labels[index] && (
                <p className="mt-2 text-sm sm:text-base text-gray-300 text-center">
                  {labels[index]}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Research = () => {
  return (
    <div className="w-full">
      {/* 🔹 Research and Innovations (R&I) */}
      <SectionWithImages
        id="R&I"
        title="Research and Innovations (R&I)"
        text="Microalgae are a diverse group of microorganisms that biosynthesize significant bioactive compounds. Our research and development department focuses on enhancing the biochemical and functional characteristics of finished products while optimizing every facet of microalgae production. Every development endeavor aims to address the unique requirements of research and business partners."
        images={[]}
        reverse={false}
      />

      {/* 🔹 Research Facility */}
      <SectionWithImages
        id="research-facility"
        title="Research Facility: Lab Scale Experiment for Microalgae Growth"
        text="Our research team has extensive experience handling various microorganisms, including microalgae strains. Our lab is equipped with all the necessary tools to sustain cultures axenically, ensuring high-quality research and development."
        images={[research1, research2]}
        reverse={true}
        titleClassName="text-2xl sm:text-3xl lg:text-4xl"
      />

      {/* 🔹 Pilot to Large-Scale Production */}
      <SectionWithImages
        id="pilot-to-large"
        title="Pilot to Large-Scale Production"
        text="Our pilot-scale facility includes closed raceway ponds and tubular photobioreactors for outdoor experimentation. We aim to develop an automated system for low-maintenance microalgal production, integrating efficient cell disruption and cost-effective post-harvesting techniques to recover valuable biomass and products. Most pre- and post-harvest processes are automated, ensuring product safety for human consumption."
        images={[]}
        reverse={false}
      />

      {/* 🔹 Closed Raceway Ponds */}
      <SectionWithImages
        id="closed-raceway-ponds"
        title="Closed Raceway Ponds"
        text="We are working on implementing closed raceway pond systems to enhance microalgae production efficiency while maintaining sustainability."
        images={[research3, research4]}
        reverse={false}
        titleClassName="text-2xl sm:text-3xl lg:text-4xl"
      />

      {/* 🔹 Tubular Photobioreactor */}
      <SectionWithImages
        id="tubular-photobioreactor"
        title="Tubular Photobioreactor"
        text="Our upcoming tubular photobioreactor system will enable controlled and efficient large-scale microalgae cultivation."
        images={[research5, research6]}
        reverse={false}
        titleClassName="text-2xl sm:text-3xl lg:text-4xl"
      />
    </div>
  );
};

export default Research;
